import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import { TalentExclusivity } from "../../talents/talentAPI";
import { AddExclusivityForm, ExclusivityFormValues } from "./AddExclusivityForm";
import {
  defaultExclusivityFormValues,
  mapEntryToExclusivityFormValues,
} from "./exclusivityMappers";

type Props = {
  isOpen: boolean;
  editingEntry?: TalentExclusivity | null;
  onSave: (formData: ExclusivityFormValues) => void;
  onClose: () => void;
  isLoading?: boolean;
};

const AddExclusivityDialog = ({
  isOpen,
  editingEntry,
  onSave,
  onClose,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const isEditing = !!editingEntry;

  const methods = useForm<ExclusivityFormValues>({
    defaultValues: defaultExclusivityFormValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
  } = methods;

  useEffect(() => {
    if (!isOpen) {
      reset(defaultExclusivityFormValues);
      return;
    }

    reset(
      editingEntry
        ? mapEntryToExclusivityFormValues(editingEntry)
        : defaultExclusivityFormValues,
    );
  }, [isOpen, editingEntry, reset]);

  const handleCancel = () => {
    onClose();
    reset(defaultExclusivityFormValues);
  };

  const title = isEditing
    ? t("talentProfile:editExclusivity")
    : t("talentProfile:newExclusivity");

  return (
    <BasicDialog
      open={isOpen}
      title={title}
      onCancel={handleCancel}
      onConfirm={handleSubmit(onSave)}
      confirmDisabled={!isDirty || !isValid || isLoading}
      content={
        <FormProvider {...methods}>
          <AddExclusivityForm isEditing={isEditing} />
        </FormProvider>
      }
      PaperProps={{ sx: { minWidth: "700px" } }}
    />
  );
};

export default AddExclusivityDialog;
